/**
 * Regime + Mode scaling utilities.
 *
 * Regime adjusts structure width (how much room you give the market).
 * Mode adjusts risk profile (how much premium you collect vs protection).
 */

import { STRIKE_STEP } from "@/lib/constants";
import type { Mode, Regime } from "@/types/options";

export const REGIME_SCALE: Record<Regime, number> = {
  range: 0.7,
  neutral: 1.0,
  trend: 1.3,
  breakout: 1.6,
};

export const MODE_SCALE: Record<Mode, { short: number; long: number }> = {
  conservative: { short: 1.2, long: 1.0 }, // shorts further OTM → less credit, higher win rate
  neutral: { short: 1.0, long: 1.0 },
  aggressive: { short: 0.8, long: 1.0 }, // shorts closer to ATM → more credit, lower win rate
};

/** Round to nearest SPX strike increment. */
export function r5(n: number): number {
  return Math.round(n / STRIKE_STEP) * STRIKE_STEP;
}

/** Apply regime+mode scaling to a base offset value.
 *  When base is 0, pass `range` to use additive scaling instead:
 *  offset = (combinedScale - 1.0) * range, so neutral (1.0) stays at 0. */
export function scale(
  base: number,
  regime: Regime,
  mode: Mode,
  isShort: boolean,
  range?: number,
): number {
  const rs = REGIME_SCALE[regime];
  const ms = isShort ? MODE_SCALE[mode].short : MODE_SCALE[mode].long;
  const combined = rs * ms;
  if (base === 0 && range != null) {
    return r5((combined - 1.0) * range);
  }
  return r5(base * combined);
}
